import { useEffect, useId, useRef, type ReactNode } from 'react';
import './Dialog.css';

/**
 * A native `<dialog>`, so the focus trap, the Escape key and the inert
 * background all come from the platform rather than from us getting them
 * subtly wrong.
 *
 * The dialog element itself carries no padding — the inner wrapper does — so
 * a click landing on the element really is a click on the backdrop.
 */
export function Dialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const node = ref.current;
    node?.showModal();
    return () => node?.close();
  }, []);

  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="dialog__body">
        <h2 className="display dialog__title" id={titleId}>
          {title}
        </h2>
        {children}
      </div>
    </dialog>
  );
}

export function DialogActions({ children }: { children: ReactNode }) {
  return <div className="dialog__actions">{children}</div>;
}
